import { useMemo, useState } from 'react';
import { CalendarClock, CalendarPlus, Footprints } from 'lucide-react';
import {
  useBoardData,
  useDayAppointments,
  useUnscheduledCheckIns,
} from '@/pages/CheckInV2/hooks';
import { STATUS_CONFIG } from '@/pages/CheckInV2/config';
import { formatTime12h } from '@/pages/CheckInV2/slots';
import WidgetCard, { EmptyLine } from './WidgetCard';

interface AppointmentsCardProps {
  onOpen: () => void;
  onSchedule: () => void;
}

/**
 * Today's appointments (soonest first) and walk-ins/drop-offs waiting,
 * from the same board data the Appointments page uses (live-updating).
 */
export default function AppointmentsCard({
  onOpen,
  onSchedule,
}: Readonly<AppointmentsCardProps>) {
  // Today, fixed for the life of the card (the board loads its month)
  const [today] = useState(() => new Date());
  const board = useBoardData(today);
  const appointments = useDayAppointments(board.events, board.checkIns, today);
  const unscheduled = useUnscheduledCheckIns(board.checkIns, today);

  const sorted = useMemo(
    () =>
      [...appointments].sort((a, b) =>
        (a.start_time ?? '99:99').localeCompare(b.start_time ?? '99:99')
      ),
    [appointments]
  );
  const waiting = unscheduled.filter(c => c.check_in_status === 'waiting');

  return (
    <WidgetCard
      title="Today's Appointments"
      icon={CalendarClock}
      badge={board.isLoading ? undefined : sorted.length}
      loading={board.isLoading}
      action={{ label: 'Open Appointments', onClick: onOpen }}
    >
      {board.error ? (
        <EmptyLine>Couldn't load today's board: {board.error}</EmptyLine>
      ) : (
        <>
          {waiting.length > 0 && (
            <button
              type="button"
              onClick={onOpen}
              className="mb-2 flex w-full items-center gap-2 rounded-md border border-tifpa-rose bg-[color-mix(in_oklch,var(--tifpa-light-rose)_50%,white)] px-2 py-1.5 text-left text-xs font-medium text-modern-charcoal hover:bg-[color-mix(in_oklch,var(--tifpa-light-rose)_80%,white)]"
            >
              <Footprints className="h-3.5 w-3.5" />
              {waiting.length} walk-in{waiting.length === 1 ? '' : 's'} or
              drop-off{waiting.length === 1 ? '' : 's'} waiting
            </button>
          )}
          {sorted.length === 0 ? (
            <EmptyLine>No appointments on the calendar today.</EmptyLine>
          ) : (
            <ul className="-mx-2 divide-y divide-slate-100">
              {sorted.slice(0, 6).map(appt => {
                const status = appt.checkIn?.check_in_status;
                return (
                  <li
                    key={appt.google_event_id}
                    className="flex items-center gap-3 px-2 py-2"
                  >
                    <span className="w-16 shrink-0 text-xs font-semibold tabular-nums text-slate-700">
                      {appt.start_time
                        ? formatTime12h(appt.start_time)
                        : 'All day'}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-900">
                        {appt.business_name || appt.event_title}
                      </span>
                      {appt.employee_name && (
                        <span className="block truncate text-xs text-slate-500">
                          with {appt.employee_name}
                        </span>
                      )}
                    </span>
                    {status && (
                      <span
                        className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_CONFIG[status].chip}`}
                      >
                        {STATUS_CONFIG[status].label}
                      </span>
                    )}
                  </li>
                );
              })}
              {sorted.length > 6 && (
                <li className="px-2 pt-2 text-xs text-slate-500">
                  +{sorted.length - 6} more today
                </li>
              )}
            </ul>
          )}
          <button
            type="button"
            onClick={onSchedule}
            className="mt-auto inline-flex items-center justify-center gap-1.5 self-start rounded-md bg-modern-charcoal px-3 py-1.5 text-xs font-semibold text-white hover:bg-modern-light-charcoal"
          >
            <CalendarPlus className="h-3.5 w-3.5" />
            Schedule Appointment
          </button>
        </>
      )}
    </WidgetCard>
  );
}
